import { use, useEffect, useRef, useState } from 'react';

import type { MessagePayload } from 'firebase/messaging';
import { onMessage } from 'firebase/messaging';

import { useQuery } from '@apollo/client/react';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { graphql } from '@/gql';

import { messaging } from '../firebase/firebaseConfig';

import { useFCMToken } from './useFCMToken';

import { useMutation } from './mutationWithSnackbar';

const USER_DEVICE_QUERY = graphql(`
  query UserDevice($userId: ID!) {
    user(id: $userId) {
      _id
      deviceRegistrationsConnection {
        edges {
          node {
            token
          }
        }
      }
    }
  }
`);

const REGISTER_DEVICE_MUTATION = graphql(`
  mutation RegisterDevice($params: RegisterDeviceForUserInput) {
    registerDeviceForUser(params: $params) {
      success
    }
  }
`);

export const useFCM = () => {
  const { currentUserId } = use(CurrentUserContext);
  const fcmToken = useFCMToken();
  const [messages, setMessages] = useState<MessagePayload[]>([]);

  const { data, loading } = useQuery(USER_DEVICE_QUERY, {
    variables: { userId: currentUserId || '' },
    skip: !currentUserId,
    fetchPolicy: 'network-only',
  });
  const [registerDevice] = useMutation(REGISTER_DEVICE_MUTATION, {
    completionMessage: 'Your device has been registered for notifications',
    hideErrorMessage: true,
  });
  const hasRegisteredRef = useRef(false);

  const deviceRegistered = Boolean(
    data?.user?.deviceRegistrationsConnection.edges.find(
      (edge) => edge.node.token === fcmToken,
    ),
  );

  useEffect(() => {
    // try and only do this when absolutely required
    const register = async () => {
      if (
        fcmToken &&
        currentUserId &&
        !hasRegisteredRef.current &&
        !loading &&
        !deviceRegistered
      ) {
        try {
          await registerDevice({
            variables: {
              params: { deviceToken: fcmToken, userId: currentUserId },
            },
          });
          hasRegisteredRef.current = true;
        } catch (error) {
          console.error('Failed to register device:', error);
          // might want to allow retries by not setting hasRegisteredRef.current = true on failure
        }
      }
    };
    register();
  }, [fcmToken, currentUserId, registerDevice, deviceRegistered, loading]);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const fcmmessaging = messaging();
      const unsubscribe = onMessage(fcmmessaging, (payload) => {
        setMessages((messages) => [...messages, payload]);
      });
      return () => unsubscribe();
    }
  }, [fcmToken]);

  return { fcmToken, messages };
};
